import { Citation, Vehicle } from '@parkingbase/ecommerce-api-test';
import { create } from 'zustand';

import { CitationService } from '@/services/citation.service';
import { logger } from '@/lib/logger';

const citationService = new CitationService();

interface CitationState {
  citations: Citation[];
  isLoading: boolean;
  isCheckingCitations: boolean;
  selectedVehicle: string;

  setSelectedVehicle: (vehicle: string) => void;

  /** Fetches all citations for the current user. */
  fetchCitations: () => Promise<void>;

  /** Returns a citation by its ID from the already-fetched list. */
  getCitationById: (id: string) => Citation | undefined;

  /**
   * Fetches all user citations, filters for valid unpaid ones matching the given vehicle's
   * license plate + state. Returns the filtered array.
   */
  checkVehicleCitations: (vehicle: Vehicle) => Promise<Citation[]>;

  clearCitations: () => void;
  reset: () => void;
}

export const useCitationStore = create<CitationState>((set, get) => ({
  citations: [],
  isLoading: false,
  isCheckingCitations: false,
  selectedVehicle: '',

  setSelectedVehicle: (vehicle: string) => set({ selectedVehicle: vehicle }),

  reset: () =>
    set({ citations: [], isLoading: false, isCheckingCitations: false, selectedVehicle: '' }),

  fetchCitations: async () => {
    set({ isLoading: true });
    try {
      const citations = await citationService.getCitationsByUserId();
      set({ citations, isLoading: false });
    } catch (error) {
      logger.error('Failed to fetch citations', error, {
        feature: 'citation-store',
        route: 'fetchCitations',
      });
      set({ isLoading: false });
    }
  },

  getCitationById: (id: string) => {
    return get().citations.find(
      (c) => c.get_id === id || c.ticketNo === id || c.code === id
    );
  },

  checkVehicleCitations: async (vehicle: Vehicle): Promise<Citation[]> => {
    try {
      set({ isCheckingCitations: true });
      const allCitations = await citationService.getCitationsByUserId();

      const filtered = allCitations.filter(
        (c) =>
          c.status === 'Valid' &&
          (c.fineAmount ?? 0) > 0 &&
          c.vehicleDetails?.lpNumber === vehicle.vehicleInfo.vehicleLicensePlate &&
          c.vehicleDetails?.state === vehicle.vehicleInfo.vehicleState
      );

      set({ isCheckingCitations: false });
      return filtered;
    } catch (error) {
      logger.error('Failed to check vehicle citations', error, {
        feature: 'citation-store',
        route: 'checkVehicleCitations',
      });
      set({ isCheckingCitations: false });
      return [];
    }
  },

  clearCitations: () => set({ citations: [] }),
}));
